import { useState } from 'react';
import { Stack, Text, TextInput, Button, Group } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconAlertCircle } from '@tabler/icons-react';
import { useAuth } from '../../hooks/useAuth';
import { setContactEmail } from '../../services/contactEmailService';
import { ApiError } from '../../services/ApiError';

type ContactEmailPreferenceProps = {
  authEmail?: string | null;
  initialContactEmail?: string;
  initialPendingContactEmail?: string;
};

/**
 * Self-persisting like GoogleCalendarPreference: calls its own endpoint and
 * reports through notifications, so its state stays out of the profile
 * snapshot and never trips the unsaved-changes pill. A new address activates
 * only after its emailed confirmation link is clicked.
 */
export const ContactEmailPreference = ({
  authEmail,
  initialContactEmail,
  initialPendingContactEmail,
}: ContactEmailPreferenceProps) => {
  const { user } = useAuth();
  const [contactEmail] = useState(initialContactEmail);
  const [pending, setPending] = useState(initialPendingContactEmail);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const activeEmail = contactEmail || authEmail || '';
  const validEmail = /^\S+@\S+$/.test(email.trim());

  const handleSubmit = async () => {
    if (!user || !validEmail) return;
    setSubmitting(true);
    try {
      const token = await user.getIdToken();
      const result = await setContactEmail(email.trim(), token);
      if (result.alreadyActive) {
        notifications.show({
          title: 'Already active',
          message: 'That address is already where your email goes.',
          color: 'blue',
          icon: <IconCheck size={18} />,
        });
      } else {
        setPending(email.trim());
        setEmail('');
        notifications.show({
          title: 'Confirmation link sent',
          message: 'Check that inbox and click the link to switch your email over.',
          color: 'green',
          icon: <IconCheck size={18} />,
        });
      }
    } catch (err) {
      notifications.show({
        title: 'Could not update email',
        message:
          err instanceof ApiError && err.statusCode === 429
            ? 'Too many attempts. Please wait a few minutes and try again.'
            : 'Something went wrong. Please try again.',
        color: 'red',
        icon: <IconAlertCircle size={18} />,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Where we send your meal plans, tips, and account notices.
      </Text>

      <Text size="sm">
        {activeEmail ? (
          <>
            Email goes to <strong>{activeEmail}</strong>
          </>
        ) : (
          <>
            <strong>No email on file.</strong> Add one below so you don't miss
            your meal plans and account updates.
          </>
        )}
      </Text>

      {pending && (
        <Text size="sm" c="orange">
          Awaiting confirmation: <strong>{pending}</strong>. Check that inbox
          for the confirmation link.
        </Text>
      )}

      <Group gap="sm" align="flex-end" wrap="wrap">
        <TextInput
          type="email"
          label={activeEmail ? 'New email address' : 'Email address'}
          placeholder="you@example.com"
          value={email}
          onChange={(event) => setEmail(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') handleSubmit();
          }}
          radius="md"
          w={{ base: '100%', sm: 300 }}
        />
        <Button
          color="orange"
          radius="md"
          onClick={handleSubmit}
          loading={submitting}
          disabled={!validEmail}
        >
          Send confirmation link
        </Button>
      </Group>
    </Stack>
  );
};
